/**
 * Gemeinsame Schnittstelle für Markdown-Eingabefelder.
 *
 * Der große Editor läuft auf CodeMirror 6 (Mehrfachcursor, Faltung), der
 * Hausaufgaben-Dialog auf einem schlichten `<textarea>`. Damit sich beide
 * exakt gleich anfühlen, sprechen `keys.ts`, `format.ts` und `slash.ts` nur
 * noch diese Schnittstelle an – nicht mehr das Textfeld direkt.
 */

export interface MdField {
  /** Kompletter Text. */
  value: string;
  readonly selectionStart: number;
  readonly selectionEnd: number;
  setSelectionRange(start: number, end: number): void;
  focus(): void;
  /** Ersetzt die aktuelle Auswahl durch `text` (undo-fähig). */
  insert(text: string): void;
  /** Löscht die aktuelle Auswahl (undo-fähig). */
  erase(): void;
  /** Ereignisse: `input`, `keydown`, `blur`, `paste`, `scroll`. */
  on(type: string, fn: (e: any) => void): void;
  /** Meldet eine Textänderung an alle `input`-Hörer. */
  emitInput(): void;
  /** Wie viele Monospace-Zeichen passen in eine Zeile? */
  columns(): number;
  /** Bildschirmposition unter dem Cursor (für das Slash-Menü). */
  caretCoords(): { left: number; bottom: number };
  /**
   * Rückt alle Auswahlbereiche ein bzw. aus – auch mehrere gleichzeitig
   * (Mehrfachcursor). Liefert `false`, wenn das Feld das nicht kann; dann
   * übernimmt die einfache Variante in keys.ts.
   */
  indentRanges?(outdent: boolean): boolean;
}

const charWidths = new Map<string, number>();

/** Breite eines Zeichens in der Schrift des Elements (Monospace vorausgesetzt). */
export function charWidthOf(cs: CSSStyleDeclaration): number {
  const font = `${cs.fontStyle} ${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}`;
  let width = charWidths.get(font);
  if (width == null) {
    const ctx = document.createElement("canvas").getContext("2d");
    if (ctx) ctx.font = `${cs.fontStyle} ${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}`;
    width = ctx ? ctx.measureText("0".repeat(20)).width / 20 : 8;
    charWidths.set(font, width);
  }
  return width || 8;
}

/** `MdField` auf Basis eines gewöhnlichen `<textarea>`. */
export function textareaField(ta: HTMLTextAreaElement): MdField {
  return {
    get value() {
      return ta.value;
    },
    set value(v: string) {
      ta.value = v;
    },
    get selectionStart() {
      return ta.selectionStart;
    },
    get selectionEnd() {
      return ta.selectionEnd;
    },
    setSelectionRange: (a, b) => ta.setSelectionRange(a, b),
    focus: () => ta.focus(),
    insert(text) {
      ta.focus();
      // execCommand hält das native Undo/Redo des Browsers intakt.
      document.execCommand("insertText", false, text);
    },
    erase() {
      ta.focus();
      document.execCommand("delete");
    },
    on: (type, fn) => ta.addEventListener(type, fn),
    emitInput: () => ta.dispatchEvent(new Event("input")),
    columns() {
      const cs = getComputedStyle(ta);
      const inner =
        ta.clientWidth - parseFloat(cs.paddingLeft || "0") - parseFloat(cs.paddingRight || "0");
      return Math.max(20, Math.floor(inner / charWidthOf(cs)));
    },
    caretCoords: () => textareaCaret(ta),
  };
}

/**
 * Cursorposition eines `<textarea>` in Bildschirmkoordinaten – gemessen über
 * einen unsichtbaren Textspiegel, weil das Element selbst nichts darüber
 * verrät.
 */
function textareaCaret(ta: HTMLTextAreaElement): { left: number; bottom: number } {
  const cs = getComputedStyle(ta);
  const div = document.createElement("div");
  const s = div.style;
  s.position = "absolute";
  s.visibility = "hidden";
  s.whiteSpace = "pre-wrap";
  s.wordWrap = "break-word";
  s.width = ta.clientWidth + "px";
  for (const p of ["fontFamily", "fontSize", "fontWeight", "lineHeight", "padding"] as const) {
    (s as unknown as Record<string, string>)[p] = (cs as unknown as Record<string, string>)[p];
  }
  div.textContent = ta.value.slice(0, ta.selectionStart);
  const span = document.createElement("span");
  span.textContent = ta.value.slice(ta.selectionStart) || ".";
  div.appendChild(span);
  document.body.appendChild(div);
  const rect = ta.getBoundingClientRect();
  const left = rect.left - ta.scrollLeft + span.offsetLeft;
  const top = rect.top - ta.scrollTop + span.offsetTop;
  const lh = parseFloat(cs.lineHeight) || 20;
  div.remove();
  return { left, bottom: top + lh };
}
